import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { faPhone, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { verifyNonProfitManually } from 'api/verification';
import * as Form from 'components/form';
import * as Tooltip from 'components/tooltip/Tooltip';
import {
    UploadBrowse,
    UploadFile,
    UploadHeadless,
    UploadProvider,
    UseFormUpload,
} from 'components/upload';
import { NONPROFIT_VERIFICATION_EXAMPLE_LETTER_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { first } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface NonProfitManualVerificationFormProps {
    afterSubmit: () => void;
}

interface NonProfitManualVerificationFormValues {
    file: UploadFile[];
    phone_number: string;
}

const NonProfitManualVerificationForm = ({ afterSubmit }: NonProfitManualVerificationFormProps) => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { id: userId } = useCurrentUser();

    const methods = Form.useGivebutterForm<NonProfitManualVerificationFormValues>({
        defaultValues: {
            file: [],
            phone_number: '',
        },
    });
    const { control, handleSubmit, path, setError, getValues } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: (values: NonProfitManualVerificationFormValues) => {
            const data = {
                account_id: accountId.toString(),
                phone_number: values.phone_number,
                file: first(getValues('file'))?.file as File,
            };

            return verifyNonProfitManually(userId, data);
        },
        onSuccess: () => {
            toast.success('Successfully submitted. Your documents will be under review.');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            afterSubmit();
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Failed to submit verification.');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof NonProfitManualVerificationFormValues, {
                        message: errors[field][0],
                    });
                }
            }
        },
    });

    return (
        <div className="flex flex-col gap-4">
            <div className="flex max-w-2xl flex-col gap-2">
                <h2 className="text-lg font-medium">Manual Verification</h2>
                <p>
                    Alternatively, you can verify your nonprofit manually by uploading your
                    organization’s IRS determination letter and phone number below for manual
                    review.
                </p>
            </div>

            <Form.Provider {...methods}>
                <form
                    onSubmit={handleSubmit(values => submit(values))}
                    className="flex max-w-md flex-col gap-4"
                >
                    <Form.Container>
                        <Form.Label
                            className="flex items-center gap-2 text-base"
                            name={path('file')}
                        >
                            IRS determination letter
                            <Tooltip.Root>
                                <Tooltip.Trigger
                                    asChild={true}
                                    className={`cursor-help text-gray-500`}
                                >
                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <div className="flex flex-col gap-1">
                                            <p>Maximum upload file size: 5MB</p>
                                            <p>
                                                Accepted file types: .doc, .docx, .pdf, .png, .jpg,
                                                .jpeg
                                            </p>
                                        </div>
                                        <Tooltip.Arrow inverse />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        </Form.Label>
                        <UploadProvider>
                            <UploadHeadless>
                                <UseFormUpload
                                    control={control}
                                    variant={'headless'}
                                    name={'file'}
                                    allowMultiple={false}
                                    allowReorder={false}
                                    strategy={'memory'}
                                    maxFileSize={'5MB'}
                                    acceptedFileTypes={[
                                        'image/*',
                                        'application/msword',
                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                        'application/pdf',
                                    ]}
                                />
                            </UploadHeadless>
                            <UploadBrowse />
                        </UploadProvider>
                        <p className="text-sm text-gray-500">
                            This is an IRS letter approving your organization’s application for
                            federal tax exemption. View{' '}
                            <a
                                href={NONPROFIT_VERIFICATION_EXAMPLE_LETTER_LINK}
                                className="text-blue-600 hover:underline"
                                target="blank"
                            >
                                Example letter
                            </a>
                        </p>
                        <Form.Error name={path('file')} />
                    </Form.Container>
                    <Form.Container>
                        <Form.Label className="text-base" name={path('phone_number')}>
                            Nonprofit Phone Number
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot control={control} name="phone_number">
                            <FontAwesomeIcon icon={faPhone} />
                            <Form.TextInputForm placeholder="Enter phone number" />
                        </Form.TextInputRoot>
                        <Form.Error name={path('phone_number')} />
                    </Form.Container>

                    <Form.SubmitButton isSubmitting={isLoading}>Submit</Form.SubmitButton>
                </form>
            </Form.Provider>
        </div>
    );
};

export { NonProfitManualVerificationForm };
